import React, { useEffect, useRef, useState, lazy, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import videoSrc from "../asset/homepage-vide-updated.mov";
import blackBackground from "../asset/blackBackground.png";
import { fetchBestSeller, fetchExplore } from "../services/api";
import { Product } from "../utiles/types";
import Carousel from "../utiles/Carousel";
import FullPageLoader from "./Home/FullPageLoader"; // Import the new loader component
import { MdCategory } from "react-icons/md";
import CategorySection from "./Home/CategorySection";

// Lazy-load sections
const VideoSection = lazy(() => import("./Home/VideoSection"));
const BestSellersSection = lazy(() => import("./Home/BestSellersSection"));
const ExploreSection = lazy(() => import("./Home/ExploreSection"));

const Home: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const [bestseller, setBestSeller] = useState<Product[]>([]);
  const [exploreProducts, setExploreProducts] = useState<Product[]>([]);
  const [wishlist, setWishlist] = useState<Product[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Track loading state for individual sections
  const [isBestSellerLoaded, setIsBestSellerLoaded] = useState(false);
  const [isExploreLoaded, setIsExploreLoaded] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 1000,
      once: true,
    });

    // Fetch wishlist from localStorage on mount
    const storedWishlist = localStorage.getItem("wishlist");
    if (storedWishlist) {
      setWishlist(JSON.parse(storedWishlist));
    }
  }, []);

  useEffect(() => {
    // Intersection Observer for video visibility
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { root: null, threshold: 0.5 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  useEffect(() => {
    // Handle video play/mute based on visibility
    const video = videoRef.current;
    if (video) {
      if (isInView) {
        video.muted = false;
        video
          .play()
          .catch((error) => console.error("Error playing video:", error));
      } else {
        video.muted = true;
        video.pause();
      }
    }
  }, [isInView, videoRef]);

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        const fetchedCategories = await fetchBestSeller();
        setBestSeller(fetchedCategories);
        setIsBestSellerLoaded(true);
      } catch (error) {
        console.error("Error fetching bestsellers:", error);
      }
    };

    if (bestseller.length === 0) fetchBestSellers();
  }, [bestseller]);

  useEffect(() => {
    if (exploreProducts.length > 0) return; // already loaded

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const fetchedExplore = await fetchExplore();
        setExploreProducts(fetchedExplore);
        console.log('Category section data:',fetchedExplore);
        setIsExploreLoaded(true);
      } catch (error) {
        console.error("Error fetching explore products:", error);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []); // run once on mount

  // Update overall loading state
  useEffect(() => {
    setIsLoading(!isBestSellerLoaded || !isExploreLoaded);
  }, [isBestSellerLoaded, isExploreLoaded]);

  const handleNavigate = (path: string) => navigate(path);
  const handleNavigate2 = useCallback(
    (id: string) => {
      navigate(`/Product/${id}`);
    },
    [navigate]
  );
  const togglePopup = () => setIsPopupOpen(!isPopupOpen);

  // If still loading, show full-page loader
  // if (isLoading) {
  //   return <FullPageLoader />;
  // }

  return (
    <>
      <div data-aos="fade-up">
        <Carousel />
      </div>

      <Suspense fallback={<div>Loading Explore Section...</div>}>
        <div data-aos="fade-up">
          <ExploreSection
            exploreProducts={exploreProducts}
            handleNavigate={handleNavigate2}
            wishlist={wishlist}
            toggleWishlist={(product) => console.log(product)}
          />
        </div>
      </Suspense>

      <Suspense fallback={<div>Loading Explore Section...</div>}>
        <div data-aos="fade-up">
          <CategorySection
            exploreProducts={exploreProducts}
            handleNavigate={handleNavigate2}
            wishlist={wishlist}
            toggleWishlist={(product) => console.log(product)}
          />
        </div>
      </Suspense>

      {/* Best Sellers Section */}
      <Suspense fallback={<div>Loading Bestseller Section...</div>}>
        <div data-aos="fade-up">
          <BestSellersSection
            bestseller={bestseller}
            handleNavigate={handleNavigate}
            toggleWishlist={(product: Product) => console.log(product)}
            wishlist={wishlist}
          />
        </div>
      </Suspense>

      {/* Video Section */}
      <Suspense fallback={<div>Loading Video Section...</div>}>
        <div>
          <VideoSection
            // videoSrc={videoSrc}
            // isPopupOpen={isPopupOpen}
            // togglePopup={togglePopup}
          />
        </div>
      </Suspense>

      {/* Explore Our Collections Section */}

      {/* Why Black Section */}
      {/* Store Location Section */}
      <section className="py-24 bg-gray-100" data-aos="fade-up">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Store Info */}
            <div className="space-y-6 text-left" data-aos="fade-right">
              <h2 className="text-4xl font-light">Maysam</h2>
              <div className="space-y-1 text-lg">
                <p>Shop no. 127, Lema Garden, Cluster associations,</p>
                <p>Jabalpur (M.P.) 482001</p>
                <p className="mt-4">11:00 am – 10:00 pm Daily</p>
              </div>
              <button
                onClick={() => window.open('https://maps.google.com/?q=Shop+no.+127,+Lema+Garden,+Cluster+associations,+Jabalpur')}
                className="inline-flex items-center space-x-2 border-b border-black pb-1 hover:border-transparent transition-colors"
              >
                <span>GET DIRECTION</span>
              </button>
            </div>

            {/* Right: Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-aos="fade-left">
              {/* Subscribe & Save */}
              <div className="space-y-4">
                <div className="h-12 w-12 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-8 h-8 items-center justify-c enter ">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                  </svg>  
                </div>
                <h3 className="text-lg font-medium">SUBSCRIBE AND SAVE ON EVERY ORDER</h3>
                <p className="text-gray-600">Unlock extra savings on eligible subscriptions</p>
              </div>

              {/* Unbeatable Selection */}
              <div className="space-y-4">
                <div className="h-12 w-12 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 12 9a2.625 2.625 0 0 0 0-4.125Z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium">UNBEATABLE SELECTION</h3>
                <p className="text-gray-600">More than 1000+ products</p>
              </div>

              {/* Easy Way to Pay */}
              <div className="space-y-4">
                <div className="h-12 w-12 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium">EASY WAY TO PAY</h3>
                <p className="text-gray-600">Find multiple payment options including UPI, Credit/Debit cards and more</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;